import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ChevronRight, Anchor, Wind, Shield, Activity, HeartPulse, TrendingUp, TrendingDown, Minus } from "lucide-react";
import useCountUp from "../lib/useCountUp.js";
import api from "../lib/api.js";

const PILLAR_ICONS = {
  alignment: { icon: Anchor,     accent: "#4ade80", short: "ALG" },
  mobility:  { icon: Wind,       accent: "#22d3ee", short: "MOB" },
  stability: { icon: Shield,     accent: "#a78bfa", short: "STA" },
  endurance: { icon: Activity,   accent: "#FBBF24", short: "END" },
  recovery:  { icon: HeartPulse, accent: "#ec4899", short: "REC" },
};

/**
 * Compact Posture Wellness Score™ hero card for the Home dashboard.
 * Pulls /wellness/today, shows the score, Posture Age delta, and 5 mini-pillar dots.
 */
export default function PostureWellnessHero() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/wellness/today").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const score = useCountUp(data?.wellness_score || 0, { duration: 1800 });

  if (!data) {
    return (
      <div className="mt-5 ps-card p-5 text-sm text-slate-400 ps-serif ps-italic">
        Loading Human Performance Score…
      </div>
    );
  }

  const age = data.posture_age;
  const ageDiff = age.years_difference;
  const ageColor = ageDiff > 0 ? "#4ade80" : ageDiff < 0 ? "#ef4444" : "#94a3b8";

  return (
    <Link
      to="/wellness"
      data-testid="home-wellness-hero"
      className="block mt-5 ps-glass-card p-5 relative overflow-hidden group"
      style={{ boxShadow: `0 1px 0 0 rgba(255,255,255,0.08) inset, 0 -1px 0 0 rgba(0,0,0,0.25) inset, 0 28px 60px -22px rgba(74,222,128,0.45)` }}
    >
      {/* Ambient mesh */}
      <div aria-hidden className="ps-mesh-drift absolute inset-[-30%] opacity-60 pointer-events-none" style={{ background: "radial-gradient(circle at 22% 25%, #4ade8044 0%, transparent 45%), radial-gradient(circle at 85% 70%, #22d3ee2e 0%, transparent 50%)", filter: "blur(22px)" }} />
      <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
        <div className="ps-shimmer-x absolute -inset-y-4 w-1/3" style={{ background: "linear-gradient(100deg, transparent 0%, #4ade8033 50%, transparent 100%)", transform: "translateX(-120%)" }} />
      </div>

      <div className="relative flex items-start justify-between">
        <div>
          <div className="text-sm uppercase tracking-[0.22em] text-[#4ade80] font-bold flex items-center gap-1.5">
            <Sparkles size={13} /> Human Performance Score™
          </div>
          <div className="ps-heading text-[3.5rem] leading-none font-extrabold ps-tabular mt-2.5 flex items-baseline gap-2.5" data-testid="home-wellness-score">
            <span>{score}</span>
            <span className="text-base text-slate-500 ps-serif ps-italic font-normal">/ 100</span>
            <DeltaPill delta={data.delta_today} />
          </div>
        </div>
        <ChevronRight size={18} className="text-slate-500 group-hover:translate-x-0.5 transition-transform" />
      </div>

      {/* Bar */}
      <div className="relative mt-3 h-1.5 rounded-full bg-[#0E1F2A] overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.min(100, data.wellness_score)}%`,
            background: "linear-gradient(90deg, #ef4444 0%, #FBBF24 35%, #4ade80 70%, #22d3ee 100%)",
            transition: "width 1.6s cubic-bezier(.2,.7,.2,1)",
            boxShadow: "0 0 12px rgba(74,222,128,0.55)",
          }}
        />
      </div>

      {/* 5 mini pillars */}
      <div className="relative mt-4 grid grid-cols-5 gap-2">
        {Object.entries(PILLAR_ICONS).map(([key, meta]) => {
          const value = data.pillars[key];
          const Icon = meta.icon;
          return (
            <div key={key} className="flex flex-col items-center" data-testid={`home-pillar-${key}`}>
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(160deg, ${meta.accent}44 0%, ${meta.accent}18 100%)`,
                  border: `1px solid ${meta.accent}55`,
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.25), 0 4px 10px -3px ${meta.accent}66`,
                }}
              >
                <Icon size={16} color={meta.accent} strokeWidth={2.4} />
              </div>
              <div className="ps-tabular text-[11px] font-extrabold mt-1" style={{ color: meta.accent }}>{Math.round(value)}</div>
              <div className="text-[8px] uppercase tracking-widest text-slate-500 font-bold">{meta.short}</div>
            </div>
          );
        })}
      </div>

      {/* Posture Age strip */}
      <div className="relative mt-4 flex items-center justify-between pt-3 border-t border-[#1B364A]">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
          Posture Age™
        </div>
        <div className="text-right">
          <span className="ps-heading text-lg font-extrabold ps-tabular" style={{ color: ageColor }} data-testid="home-posture-age">
            {age.posture_age}
          </span>
          <span className="text-xs text-slate-500 ms-1.5 ps-serif ps-italic">
            vs {age.chronological_age}
          </span>
          {ageDiff !== 0 && (
            <span
              className="inline-block ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold ps-tabular"
              style={{ background: `${ageColor}1f`, color: ageColor, border: `1px solid ${ageColor}44` }}
            >
              {ageDiff > 0 ? "−" : "+"}{Math.abs(ageDiff)}y
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function DeltaPill({ delta }) {
  const d = Number(delta) || 0;
  const Icon = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
  const color = d > 0 ? "#4ade80" : d < 0 ? "#ef4444" : "#94a3b8";
  return (
    <span
      data-testid="home-wellness-delta"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ps-tabular ps-fade-up"
      style={{
        background: `${color}1f`,
        color,
        border: `1px solid ${color}44`,
        animation: "ps-fade-up .9s ease-out .4s both",
      }}
      title={d === 0 ? "Same as yesterday" : `${d > 0 ? "Up" : "Down"} ${Math.abs(d)} vs yesterday`}
    >
      <Icon size={11} />
      {d > 0 ? "+" : ""}{d} today
    </span>
  );
}
